import React, { FC } from 'react';
import { Pill, PillProps } from '@clearcover/atlas';
import { QuoteSearchResults as QuoteSearchResultsType } from 'Shared/services/Reznor/PostSearch';
import { formatPhoneNumber, formatDateShort } from 'Shared/utils';
import { Table } from 'Shared/components/Table';
import { QUOTE_APP_ROUTE } from 'Shared/constants/routes';
import { LoadingQuotes, Error, NotFound } from '../statuses';
import styles from './styles.module.css';

type Props = {
  isError: boolean;
  isLoading: boolean;
  quoteResults: QuoteSearchResultsType['agentQuotes'];
  isCreatingQuote: boolean;
  fetchResults: () => void;
  createQuote: () => void;
};

type StatusDisplayType = {
  variant: PillProps['variant'];
  text: string;
};

const statusMap: { [key: string]: StatusDisplayType } = {
  open: {
    variant: 'success',
    text: 'In Progress',
  },
  dead: {
    variant: 'disabled',
    text: 'No Rate',
  },
  out_of_market: {
    variant: 'disabled',
    text: 'Out of Market',
  },
  bound: {
    variant: 'disabled',
    text: 'Bound',
  },
  stop: {
    variant: 'disabled',
    text: 'Stopped',
  },
  third_party_in_progress: {
    variant: 'success',
    text: 'In Progress',
  },
  bind_in_progress: {
    variant: 'success',
    text: 'In Progress',
  },
} as const;

const QuoteSearchResults: FC<Props> = ({
  isError,
  isLoading,
  quoteResults,
  fetchResults,
  createQuote,
  isCreatingQuote,
}) => {
  if (isError) return <Error onTryAgainClick={fetchResults} />;
  if (isLoading) return <LoadingQuotes />;
  if (quoteResults.length === 0) {
    return (
      <NotFound
        onCreateNewQuoteClick={createQuote}
        isLoading={isCreatingQuote}
      />
    );
  }

  return (
    <Table
      name="quote-search-table"
      columnWidths={['170px', 1, 1, 1, 2, 1, '60px', 1, 1, '106px']}
      headers={[
        'Quote Number',
        'Status',
        'First Name',
        'Last Name',
        'Email',
        'Phone',
        'State',
        'Producer',
        'Last Modified',
        ' ',
      ]}
      rows={quoteResults.map(result => {
        const isActive = !['dead', 'out_of_market'].includes(result.status);
        const status = statusMap[result.status];

        return {
          key: result.id,
          className: isActive ? undefined : styles.inactiveRow,
          data: [
            result.quoteNumber,
            status && (
              <Pill
                variant={status.variant}
                className={styles.policyStatus}
                title={`${status.text}`}
              >
                {status.text}
              </Pill>
            ),
            result.firstName || 'N/A',
            result.lastName || 'N/A',
            result.email || 'N/A',
            result.phone ? formatPhoneNumber(result.phone) : 'N/A',
            result.state || 'N/A',
            result.producer,
            result.updatedAt ? formatDateShort(result.updatedAt) : 'N/A',
            <RowAction
              isActive={isActive}
              status={result.status}
              applicationId={result.id}
              policyId={result.oneIncPolicyId}
            />,
          ],
        };
      })}
      hasPagination
    />
  );
};

export { QuoteSearchResults };

type RowActionProps = {
  isActive: boolean;
  status: string;
  applicationId?: string;
  policyId?: string | null;
};

const RowAction: FC<RowActionProps> = ({
  isActive,
  status,
  applicationId,
  policyId,
}) => {
  if (status === 'bound' && policyId) {
    return (
      <a
        href={`/policies/${policyId}`}
        className={styles.viewPolicy}
        data-testid="view-policy-link"
      >
        View Policy
      </a>
    );
  } else if (isActive) {
    return (
      <a
        href={`${QUOTE_APP_ROUTE}/${applicationId}`}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.continue}
        data-testid="view-quote-link"
      >
        Continue
      </a>
    );
  }
  return <></>;
};
